/**
 * Journal page route
 *
 * Multi-mode page with a segmented control: Events | Medications | Doctor Visits.
 * On small screens, shows a top segmented control to switch between
 * an events timeline, the medications management view, and doctor visits.
 * The event logging form is available behind a "New Event" button in the Events tab.
 * MedicationsTab provides its own "New Medication" CTA on all screen sizes.
 *
 * Desktop shows all tabs inline via the same segmented control.
 *
 * Supports deep-linking to a specific tab via `?tab=medications` or
 * `?tab=doctor-visits` query param. Default tab is "events".
 *
 * Spec reference: docs/medication-events-overhaul-v2.md:191-203
 * Spec reference: docs/doctor-visit-prep-redesign.md:66-82 (T5.1)
 */
import { useState, useCallback } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { CheckCircle, Plus } from "lucide-react";
import { EventForm, EventDetailDialog, EventsHistoryTimeline } from "@/components/events";
import type { SwitchMedicationFormData, DoseChangeMedicationFormData, StartMedicationFormData } from "@/components/events";
import { MedicationsTab } from "@/components/medications";
import { DoctorVisitsTab } from "@/components/doctor-visits";
import { useCreateEvent, useEvent, useUpdateEvent, useDeleteEvent } from "@/hooks";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogScrollBody, DialogTitle } from "@/components/ui/dialog";
import type { CreateEventRequest, UpdateEventRequest } from "@floline/api-client";
import { getApiClient } from "@/lib/api-client";

/** Active tab in the Journal page segmented control */
type EventsTab = "events" | "medications" | "doctor-visits";

// ============================================================================
// Segmented Control
// ============================================================================

/**
 * Segmented control for switching between Events, Medications, and Doctor Visits tabs.
 * Follows the same pattern as ViewModeToggle in timeline.tsx:
 * pill-shaped container with Button toggles, aria-pressed for a11y.
 */
function EventsTabControl({
  activeTab,
  onChange,
}: {
  activeTab: EventsTab;
  onChange: (tab: EventsTab) => void;
}) {
  const { t } = useTranslation("events");

  return (
    <div
      className="flex justify-center gap-1 p-1 bg-muted rounded-lg w-full max-w-md mx-auto"
      role="tablist"
      aria-label={t("tabs.ariaLabel")}
    >
      <Button
        variant={activeTab === "events" ? "default" : "ghost"}
        size="sm"
        onClick={() => onChange("events")}
        aria-pressed={activeTab === "events"}
        role="tab"
        className="flex-1"
      >
        {t("tabs.events")}
      </Button>
      <Button
        variant={activeTab === "medications" ? "default" : "ghost"}
        size="sm"
        onClick={() => onChange("medications")}
        aria-pressed={activeTab === "medications"}
        role="tab"
        className="flex-1"
      >
        {t("tabs.medications")}
      </Button>
      <Button
        variant={activeTab === "doctor-visits" ? "default" : "ghost"}
        size="sm"
        onClick={() => onChange("doctor-visits")}
        aria-pressed={activeTab === "doctor-visits"}
        role="tab"
        className="flex-1"
      >
        {t("tabs.doctorVisits")}
      </Button>
    </div>
  );
}

// ============================================================================
// Medications Tab (T2.6)
// ============================================================================

// Implemented as <MedicationsTab /> from @/components/medications

// Mobile FAB has been removed (T5.11).
// EventForm now always shows its own inline submit/cancel buttons.
// MedicationsTab provides its own "New Medication" button on all screen sizes.

// ============================================================================
// Main Page Component
// ============================================================================

/**
 * Journal page component
 *
 * Multi-mode view with segmented control for Events, Medications, and Doctor Visits tabs.
 * Premium guided event creation with atmospheric V1 surface treatment.
 * Supports deep-linking via `?tab=medications` or `?tab=doctor-visits` query param.
 */
function NewEventPage() {
  const { t } = useTranslation("events");
  const navigate = useNavigate();
  const { createEvent, isCreating } = useCreateEvent();
  const { updateEvent, isUpdating } = useUpdateEvent();
  const { deleteEvent, isDeleting } = useDeleteEvent();
  const search = Route.useSearch();
  const [isSuccess, setIsSuccess] = useState(false);
  const [eventFormOpen, setEventFormOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const activeTab: EventsTab =
    search.tab === "medications"
      ? "medications"
      : search.tab === "doctor-visits"
        ? "doctor-visits"
        : "events";
  const { event: selectedEvent } = useEvent(selectedEventId);

  const handleTabChange = useCallback(
    (tab: EventsTab) => {
      void navigate({
        to: "/events/new",
        search: (prev) => ({
          ...prev,
          tab: tab === "events" ? undefined : tab,
        }),
      });
    },
    [navigate]
  );

  const finishEventAction = useCallback(() => {
    setIsSuccess(true);
    setEventFormOpen(false);
    setTimeout(() => {
      setIsSuccess(false);
    }, 2000);
  }, []);

  /** Handle form submission */
  const handleSubmit = useCallback(async (data: CreateEventRequest | UpdateEventRequest) => {
    // In create mode, data is always a full CreateEventRequest
    await createEvent(data as CreateEventRequest);
    finishEventAction();
  }, [createEvent, finishEventAction]);

  /** Handle cancel */
  const handleCancel = useCallback(() => {
    setEventFormOpen(false);
  }, []);

  /** Handle medication switch submission (T2.2) */
  const handleSwitchMedication = useCallback(
    async (data: SwitchMedicationFormData) => {
      await getApiClient().switchMedication(data.fromMedicationId, data.request);
      finishEventAction();
    },
    [finishEventAction]
  );

  /** Handle medication dose change submission (T2.3) */
  const handleDoseChangeMedication = useCallback(
    async (data: DoseChangeMedicationFormData) => {
      await getApiClient().updateMedication(data.medicationId, data.request);
      finishEventAction();
    },
    [finishEventAction]
  );

  /** Handle start medication submission (T2.4, T5.2) – calls POST /api/medications */
  const handleStartMedication = useCallback(
    async (data: StartMedicationFormData) => {
      await getApiClient().createMedication({
        displayName: data.displayName,
        startDate: data.startDate,
        ...(data.doseValue !== undefined && { doseValue: data.doseValue }),
        ...(data.doseUnit && { doseUnit: data.doseUnit }),
        ...(data.route && { route: data.route }),
        ...(data.notes && { notes: data.notes }),
        ...(data.showInTrends !== undefined && { showInTrends: data.showInTrends }),
      });
      finishEventAction();
    },
    [finishEventAction]
  );

  const handleEventClick = useCallback((eventId: string) => {
    setSelectedEventId(eventId);
    setEventDialogOpen(true);
  }, []);

  const handleEventDialogClose = useCallback((open: boolean) => {
    setEventDialogOpen(open);
    if (!open) {
      setSelectedEventId(null);
    }
  }, []);

  const handleUpdateEvent = useCallback(
    async (id: string, eventData: Parameters<typeof updateEvent>[0]["data"]) => {
      await updateEvent({ id, data: eventData });
    },
    [updateEvent]
  );

  const handleDeleteEvent = useCallback(
    async (id: string) => {
      await deleteEvent(id);
      setEventDialogOpen(false);
      setSelectedEventId(null);
    },
    [deleteEvent]
  );

  return (
    <div className="min-h-screen bg-background">
      <section className="py-6 px-4 sm:py-10">
        {/* Segmented control: Events | Medications | Doctor Visits */}
        <div className="mb-6">
          <EventsTabControl activeTab={activeTab} onChange={handleTabChange} />
        </div>

        {/* Tab content */}
        {activeTab === "events" ? (
          <div className="w-full max-w-lg md:max-w-3xl lg:max-w-5xl mx-auto space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-base font-semibold tracking-tight">{t("tabs.events")}</h2>
                <p className="text-sm text-muted-foreground">{t("list.eventsPageDescription")}</p>
              </div>
              <Button
                onClick={() => setEventFormOpen(true)}
                className="gap-1.5"
                variant="outline"
                size="sm"
              >
                <Plus className="h-4 w-4" />
                {t("fab.logEvent")}
              </Button>
            </div>

            {isSuccess && (
              <div className="inline-flex items-center gap-2 rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary">
                <CheckCircle className="h-4 w-4" />
                <span>{t("new.successInline")}</span>
              </div>
            )}

            <EventsHistoryTimeline onEventClick={handleEventClick} />
          </div>
        ) : activeTab === "medications" ? (
          <MedicationsTab />
        ) : (
          <DoctorVisitsTab />
        )}
      </section>

      <EventDetailDialog
        event={selectedEvent}
        open={eventDialogOpen}
        onOpenChange={handleEventDialogClose}
        onUpdate={handleUpdateEvent}
        onDelete={handleDeleteEvent}
        isUpdating={isUpdating}
        isDeleting={isDeleting}
      />

      <Dialog open={eventFormOpen} onOpenChange={setEventFormOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("new.title")}</DialogTitle>
          </DialogHeader>
          <DialogScrollBody className="pb-1">
            <EventForm
              onSubmit={handleSubmit}
              onSwitchMedication={handleSwitchMedication}
              onDoseChangeMedication={handleDoseChangeMedication}
              onStartMedication={handleStartMedication}
              onCancel={handleCancel}
              isSubmitting={isCreating}
              submitLabel={t("new.submit")}
              hideMedicationTypes
            />
          </DialogScrollBody>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Search params for the Journal page */
export interface JournalSearchParams {
  tab?: EventsTab;
}

/**
 * Validate search params for the Journal page.
 * Supports `?tab=medications` or `?tab=doctor-visits` to deep-link to those tabs.
 * Invalid or missing values default to the Events tab.
 */
function validateJournalSearchParams(
  search: Record<string, unknown>
): JournalSearchParams {
  const result: JournalSearchParams = {};
  if (search.tab === "medications") {
    result.tab = "medications";
  } else if (search.tab === "doctor-visits") {
    result.tab = "doctor-visits";
  }
  // "events" is the default; no need to set explicitly
  return result;
}

export const Route = createFileRoute("/_authenticated/events/new")({
  component: NewEventPage,
  validateSearch: validateJournalSearchParams,
});
